import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { environment } from '../../../environments/environment';
import { InventoryService } from '../inventory/inventory.service';
import { GrnService } from '../grn/grn.service';
import { NgbAccordionItem } from '@ng-bootstrap/ng-bootstrap';
import { ProductsService } from '../products/products.service';
import { ManufacturerService } from '../manufacturers/manufacturers.service';
import { VendorService } from '../vendors/vendor.service';

import { map, catchError, tap, filter } from 'rxjs/operators';

// --- INTERFACES ---
interface FilterOption {
  id: string;
  name: string;
}

interface ProductFilters {
  vendors: FilterOption[];
  manufacturers: FilterOption[];
  brands: FilterOption[];
  categories: FilterOption[];
  subCategories: FilterOption[];
  storeLocations: FilterOption[];
}
/** One completed stock take (device session or Excel upload) in the picker. */
interface StockTakeOption {
  id: string;
  kind: 'SESSION' | 'DRAFT';
  number: string;
  mode: 'FULL' | 'PARTIAL';
  completedAt: string;
  storeId: string;
  storeName: string;
  storeCode: string;
}
// --- END INTERFACES ---

// Consolidated Report Service (Kept here as it was in your provided file)
@Component({
  template: '',
})
export class ReportsService {
  private apiUrl = `${environment.apiUrl}/reports`;
  private baseApiUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private manufacturerService: ManufacturerService,
    private productService: ProductsService,
    private vendorService: VendorService,
    private inventoryService: InventoryService,
  ) { }

  exportReport(endpoint: string, payload: any): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/${endpoint}/export-xlsx`, payload, {
      responseType: 'blob',
    });
  }

  /**
   * Same export, but emitting HttpEvents so the caller can show real progress.
   * Large exports can take a while with no visible change otherwise - the
   * server spends most of that time building the workbook before a single byte
   * is sent, so the caller must handle "no total known yet" gracefully.
   */
  exportReportWithProgress(
    endpoint: string,
    payload: any,
  ): Observable<HttpEvent<Blob>> {
    return this.http.post(`${this.apiUrl}/${endpoint}/export-xlsx`, payload, {
      responseType: 'blob',
      observe: 'events',
      reportProgress: true,
    });
  }

  /** Completed stock takes for the shrinkage report picker (both kinds). */
  listShrinkageStockTakes(storeId?: string): Observable<StockTakeOption[]> {
    const params: Record<string, string> = storeId ? { storeId } : {};
    return this.http.get<StockTakeOption[]>(
      `${this.apiUrl}/stock-take-shrinkage/stock-takes`,
      { params },
    );
  }

  // New method implementation using InventoryService's getAllLocations()
  getAllProductFilters(): Observable<ProductFilters> {
    return forkJoin({
      // 1. Vendors
      vendors: this.vendorService.getAllVendorsList().pipe(
        catchError((error) => {
          console.error('Error fetching vendors:', error);
          return of([]);
        }),
      ),

      // 2. Manufacturers
      manufacturers: this.manufacturerService.getManufacturers().pipe(
        map(
          (res: any) =>
            res?.data?.map((m: any) => ({
              id: m.id,
              name: m.name,
            })) || [],
        ),
        catchError((error) => {
          console.error('Error fetching manufacturers:', error);
          return of([]);
        }),
      ),

      // 3. Brands
      brands: this.manufacturerService.getAllManufacturersBrands().pipe(
        map(
          (res: any) =>
            res?.map((b: any) => ({
              id: b.id,
              name: b.name,
            })) || [],
        ),
        catchError((error) => {
          console.error('Error fetching brands:', error);
          return of([]);
        }),
      ),

      // 4. Categories
      categories: this.productService.getCategories().pipe(
        map(
          (res: any) =>
            res?.data?.map((c: any) => ({
              id: c.id,
              name: c.name,
            })) || [],
        ),
        catchError((error) => {
          console.error('Error fetching categories:', error);
          return of([]);
        }),
      ),

      // 5. Sub Categories
      subCategories: this.productService.getAllSubcategories().pipe(
        map(
          (res: any) =>
            res?.data?.map((sc: any) => ({
              id: sc.id,
              name: sc.name,
            })) || [],
        ),
        catchError((error: any) => {
          console.error('Error fetching subcategories:', error);
          return of([]);
        }),
      ),

      // 🛑 ADDED: New Store Locations filter API call
      storeLocations: this.inventoryService.getAllStores().pipe(
        map(
          (res: any) =>
            // Assuming response structure is { data: [{ id, name }, ...] }
            res?.map((l: any) => ({
              id: l.id,
              name: l.name,
            })) || [],
        ),
        catchError((error) => {
          console.error('Error fetching store locations:', error);
          return of([]);
        }),
      ),
    }).pipe(
      // Ensure the result structure matches the ProductFilters interface
      map((result: any) => ({
        vendors: result.vendors,
        manufacturers: result.manufacturers,
        brands: result.brands,
        categories: result.categories,
        subCategories: result.subCategories,
        // ADDED: Include the new storeLocations property in the final map
        storeLocations: result.storeLocations,
      })),
    ) as Observable<ProductFilters>; // Cast the final output to the correct type
  }
}

type ReportType =
  | 'stock'
  | 'purchase'
  | 'poWiseSummary'
  | 'poItemwise'
  | 'grnWiseSummary'
  | 'grnItemwise'
  | 'transferInSummary'
  | 'transferInItemwise'
  | 'mbqShrinkage'
  | 'transferOutSummary'
  | 'transferOutItemwise'
  | 'prnSummary'
  | 'prnItemwise'
  | 'salesSummary'
  | 'salesDetails'
  | 'transferToTiSummary'
  | 'poVsGrnSkuFill'
  | 'poVsGrnVendorFill'
  | 'poVsGrnAmountFill'
  | 'supplierPurchaseSummary'
  | 'cancelledGrnSummary'
  | 'ginItemwise'
  | 'monthWiseSummary'
  | 'ginVsGrn';

@Component({
  selector: 'app-report-export',
  templateUrl: './reports-list.component.html',
  standalone: true,
  imports: [FormsModule, CommonModule, NgbAccordionItem],
  providers: [ReportsService],
})
export class ReportsComponent implements OnInit {
  showReportModal = false;
  currentReportType: ReportType | null = null;

  // Download feedback. 'preparing' covers the stretch where the server is
  // still building the workbook and no bytes have arrived yet.
  downloadPhase: 'idle' | 'preparing' | 'downloading' = 'idle';
  downloadProgress = 0; // percent, 0 when the total size is unknown
  downloadedBytes = 0;

  fromDate: string | null = null;
  toDate: string | null = null;
  storeId: string[] = [];
  isRetailUser: boolean = false;
  isClinicClient: boolean = false;

  isLoading = false;
  userType: string | null = null;

  // Filter properties
  isLoadingFilters: boolean = false;
  productFilters: ProductFilters = {
    vendors: [],
    manufacturers: [],
    brands: [],
    categories: [],
    subCategories: [],
    storeLocations: [],
  };

  selectedVendors: string[] = [];
  selectedManufacturers: string[] = [];
  selectedBrands: string[] = [];
  selectedCategories: string[] = [];
  selectedSubCategories: string[] = [];
  selectedPreset: string | null = null;
  selectedStockPreset: string | null = null;

  // ✅ Location Filter State
  selectedStoreLocations: string[] = [];
  allLocationsSelected: boolean = false;

  // Search filters
  vendorSearchTerm: string = '';
  manufacturerSearchTerm: string = '';
  brandSearchTerm: string = '';
  categorySearchTerm: string = '';
  subCategorySearchTerm: string = '';
  locationSearchTerm: string = ''; // ✅ Location Search Term

  // Select all states
  allVendorsSelected: boolean = false;
  allManufacturersSelected: boolean = false;
  allBrandsSelected: boolean = false;
  allCategoriesSelected: boolean = false;
  allSubCategoriesSelected: boolean = false;

  // --- Stock Take Shrinkage report (own modal: it is keyed to a completed
  // stock take, not a date range) ---
  showShrinkageModal = false;
  shrinkStoreId = '';
  shrinkStockTakes: StockTakeOption[] = [];
  shrinkLoadingList = false;
  /** `kind:id` of the selected stock take, '' when none. */
  shrinkSelected = '';
  /** 'auto' = latest completed stock take before the selected one. */
  shrinkPrevSelected = 'auto';
  shrinkAuditor = '';
  shrinkManager = '';

  constructor(
    private toastr: ToastrService,
    private reportsService: ReportsService,
    private inventoryService: InventoryService,
    private grnService: GrnService,
  ) { }

  ngOnInit(): void {
    this.getStoreIdFromLocalStorage();
    this.loadProductFilters();
  }

  setStockPreset(preset: string): void {
    const today = new Date();
    let from: Date, to: Date;

    switch (preset) {
      case 'Daily':
        from = to = today;
        break;
      case 'Monthly':
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        to = today;
        break;
      case 'Yearly':
        from = new Date(today.getFullYear(), 0, 1);
        to = today;
        break;
      default:
        return;
    }

    this.selectedStockPreset = preset;
    this.fromDate = from.toISOString().split('T')[0];
    this.toDate = to.toISOString().split('T')[0];
  }

  setRelative(preset: string): void {
    const today = new Date();
    let from: Date, to: Date;

    switch (preset) {
      case 'today':
        from = to = today;
        break;
      case 'yesterday':
        from = to = new Date(today);
        from.setDate(today.getDate() - 1);
        break;
      case 'weekToDate':
        from = new Date(today);
        from.setDate(today.getDate() - today.getDay());
        to = today;
        break;
      case 'monthToDate':
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        to = today;
        break;
      case 'yearToDate':
        from = new Date(today.getFullYear(), 0, 1);
        to = today;
        break;
      case 'previousWeek':
        to = new Date(today);
        to.setDate(today.getDate() - today.getDay() - 1);
        from = new Date(to);
        from.setDate(to.getDate() - 6);
        break;
      case 'previousMonth':
        from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        to = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'previousYear':
        from = new Date(today.getFullYear() - 1, 0, 1);
        to = new Date(today.getFullYear() - 1, 11, 31);
        break;
      default:
        return;
    }

    this.selectedPreset = preset;
    this.fromDate = from.toISOString().split('T')[0];
    this.toDate = to.toISOString().split('T')[0];
  }

  loadProductFilters(): void {
    this.isLoadingFilters = true;
    this.reportsService.getAllProductFilters().subscribe({
      next: (data: ProductFilters) => {
        this.productFilters = data;
        this.isLoadingFilters = false;
      },
      error: (err) => {
        console.error('Failed to load product filters:', err);
        this.toastr.error('Failed to load filter options.');
        this.isLoadingFilters = false;
      },
    });
  }

  private reportMap: {
    [key in Exclude<ReportType, 'stock' | 'purchase'>]: {
      title: string;
      endpoint: string;
    };
  } = {
      poWiseSummary: { title: 'PO-wise Summary', endpoint: 'po-wise-summary' },
      poItemwise: { title: 'PO Item-wise Detail', endpoint: 'po-itemwise' },
      grnWiseSummary: { title: 'GRN-wise Summary', endpoint: 'grn-wise-summary' },
      grnItemwise: { title: 'GRN Item-wise Detail', endpoint: 'grn-itemwise' },
      supplierPurchaseSummary: {
        title: 'Supplier Purchase Summary',
        endpoint: 'supplier-purchase-summary',
      },
      cancelledGrnSummary: {
        title: 'Cancelled GRN Summary',
        endpoint: 'cancelled-grn-summary',
      },
      ginItemwise: {
        title: 'GIN Item-wise',
        endpoint: 'gin-itemwise',
      },
      monthWiseSummary: {
        title: 'Month-wise Summary',
        endpoint: 'month-wise-summary',
      },
      ginVsGrn: {
        title: 'GIN vs GRN',
        endpoint: 'gin-vs-grn',
      },
      transferInSummary: {
        title: 'Transfer-In Summary',
        endpoint: 'transfer-in-summary',
      },
      transferInItemwise: {
        title: 'Transfer-In Item-wise',
        endpoint: 'transfer-in-itemwise',
      },
      transferOutSummary: {
        title: 'Transfer-Out Summary',
        endpoint: 'transfer-out-summary',
      },
      transferOutItemwise: {
        title: 'Transfer-Out Item-wise',
        endpoint: 'transfer-out-itemwise',
      },
      prnSummary: {
        title: 'PRN Summary',
        endpoint: 'prn-summary',
      },
      prnItemwise: {
        title: 'PRN Item-wise',
        endpoint: 'prn-itemwise-summary',
      },
      mbqShrinkage: {
        title: 'MBQ Shrinkage',
        endpoint: 'mbq-shrinkage',
      },
      salesSummary: {
        title: 'Sales Summary',
        endpoint: 'sales-summary',
      },
      salesDetails: {
        title: 'Sales Details',
        endpoint: 'sales-detail',
      },
      transferToTiSummary: {
        title: 'Combined TO / TI Summary',
        endpoint: 'transfer-to-ti-summary',
      },
      poVsGrnSkuFill: {
        title: 'PO vs GRN SKU-wise Fill',
        endpoint: 'po-vs-grn-sku-fill',
      },
      poVsGrnVendorFill: {
        title: 'PO vs GRN Vendor-wise Fill',
        endpoint: 'po-vs-grn-vendor-fill',
      },
      poVsGrnAmountFill: {
        title: 'PO vs GRN Amount-wise Fill',
        endpoint: 'po-vs-grn-amount-fill',
      },
    };

  private getStoreIdFromLocalStorage(): string {
    const userKey = 'user';

    try {
      const userJson = localStorage.getItem(userKey);

      if (userJson) {
        const userObject = JSON.parse(userJson);

        if (userObject && userObject.storeId) {
          this.userType = userObject.clientType;
          this.isRetailUser = this.userType === 'Retail';
          this.isClinicClient = this.userType === 'Clinic';
          return userObject.storeId;
        }
      }
    } catch (e) {
      console.error('Error parsing user data from localStorage:', e);
      this.toastr.error('Failed to load store access data.');
    }

    return '';
  }

  get currentReportTitle(): string {
    if (!this.currentReportType) return '';

    if (this.currentReportType === 'stock') return 'Stock Ledger';
    if (this.currentReportType === 'purchase') return 'Purchase Tax';

    return this.reportMap[this.currentReportType].title;
  }

  // Filter getters
  get filteredLocations(): FilterOption[] {
    const term = this.locationSearchTerm.toLowerCase();
    return this.productFilters.storeLocations.filter((loc) =>
      loc.name.toLowerCase().includes(term),
    );
  }

  get filteredVendors(): FilterOption[] {
    return this.productFilters.vendors.filter((v) =>
      v.name.toLowerCase().includes(this.vendorSearchTerm.toLowerCase()),
    );
  }

  get filteredManufacturers(): FilterOption[] {
    return this.productFilters.manufacturers.filter((m) =>
      m.name.toLowerCase().includes(this.manufacturerSearchTerm.toLowerCase()),
    );
  }

  get filteredBrands(): FilterOption[] {
    // 💡 FIX: Filter the brands based on the brandSearchTerm
    return this.productFilters.brands.filter((b) =>
      b.name.toLowerCase().includes(this.brandSearchTerm.toLowerCase()),
    );
  }

  get filteredCategories(): FilterOption[] {
    return this.productFilters.categories.filter((c) =>
      c.name.toLowerCase().includes(this.categorySearchTerm.toLowerCase()),
    );
  }

  get filteredSubCategories(): FilterOption[] {
    return this.productFilters.subCategories.filter((sc) =>
      sc.name.toLowerCase().includes(this.subCategorySearchTerm.toLowerCase()),
    );
  }

  // Toggle individual item
  toggleLocation(id: string): void {
    const index = this.selectedStoreLocations.indexOf(id);
    if (index > -1) {
      this.selectedStoreLocations.splice(index, 1);
    } else {
      this.selectedStoreLocations.push(id);
    }
    this.updateSelectAllState('location');
  }

  toggleVendor(id: string): void {
    const index = this.selectedVendors.indexOf(id);
    if (index > -1) {
      this.selectedVendors.splice(index, 1);
    } else {
      this.selectedVendors.push(id);
    }
    this.updateSelectAllState('vendor');
  }

  toggleManufacturer(id: string): void {
    const index = this.selectedManufacturers.indexOf(id);
    if (index > -1) {
      this.selectedManufacturers.splice(index, 1);
    } else {
      this.selectedManufacturers.push(id);
    }
    this.updateSelectAllState('manufacturer');
  }

  toggleBrand(id: string): void {
    const index = this.selectedBrands.indexOf(id);
    if (index > -1) {
      this.selectedBrands.splice(index, 1);
    } else {
      this.selectedBrands.push(id);
    }
    this.updateSelectAllState('brand');
  }

  toggleCategory(id: string): void {
    const index = this.selectedCategories.indexOf(id);
    if (index > -1) {
      this.selectedCategories.splice(index, 1);
    } else {
      this.selectedCategories.push(id);
    }
    this.updateSelectAllState('category');
  }

  toggleSubCategory(id: string): void {
    const index = this.selectedSubCategories.indexOf(id);
    if (index > -1) {
      this.selectedSubCategories.splice(index, 1);
    } else {
      this.selectedSubCategories.push(id);
    }
    this.updateSelectAllState('subCategory');
  }

  // Check if item is selected
  isLocationSelected(id: string): boolean {
    return this.selectedStoreLocations.includes(id);
  }

  isVendorSelected(id: string): boolean {
    return this.selectedVendors.includes(id);
  }

  isManufacturerSelected(id: string): boolean {
    return this.selectedManufacturers.includes(id);
  }

  isBrandSelected(id: string): boolean {
    return this.selectedBrands.includes(id);
  }

  isCategorySelected(id: string): boolean {
    return this.selectedCategories.includes(id);
  }

  isSubCategorySelected(id: string): boolean {
    return this.selectedSubCategories.includes(id);
  }

  // Toggle select all
  toggleSelectAllLocations(): void {
    if (this.allLocationsSelected) {
      this.selectedStoreLocations = [];
      this.allLocationsSelected = false;
    } else {
      this.selectedStoreLocations = this.productFilters.storeLocations.map(
        (l) => l.id,
      );
      this.allLocationsSelected = true;
    }
  }

  toggleSelectAllVendors(): void {
    if (this.allVendorsSelected) {
      this.selectedVendors = [];
      this.allVendorsSelected = false;
    } else {
      this.selectedVendors = this.productFilters.vendors.map((v) => v.id);
      this.allVendorsSelected = true;
    }
  }

  toggleSelectAllManufacturers(): void {
    if (this.allManufacturersSelected) {
      this.selectedManufacturers = [];
      this.allManufacturersSelected = false;
    } else {
      this.selectedManufacturers = this.productFilters.manufacturers.map(
        (m) => m.id,
      );
      this.allManufacturersSelected = true;
    }
  }

  toggleSelectAllBrands(): void {
    if (this.allBrandsSelected) {
      this.selectedBrands = [];
      this.allBrandsSelected = false;
    } else {
      this.selectedBrands = this.productFilters.brands.map((b) => b.id);
      this.allBrandsSelected = true;
    }
  }

  toggleSelectAllCategories(): void {
    if (this.allCategoriesSelected) {
      this.selectedCategories = [];
      this.allCategoriesSelected = false;
    } else {
      this.selectedCategories = this.productFilters.categories.map((c) => c.id);
      this.allCategoriesSelected = true;
    }
  }

  toggleSelectAllSubCategories(): void {
    if (this.allSubCategoriesSelected) {
      this.selectedSubCategories = [];
      this.allSubCategoriesSelected = false;
    } else {
      this.selectedSubCategories = this.productFilters.subCategories.map(
        (sc) => sc.id,
      );
      this.allSubCategoriesSelected = true;
    }
  }

  // Update select all state
  private updateSelectAllState(type: string): void {
    switch (type) {
      case 'location': // ✅ ADDED
        this.allLocationsSelected =
          this.selectedStoreLocations.length ===
          this.productFilters.storeLocations.length;
        break;
      case 'vendor':
        this.allVendorsSelected =
          this.selectedVendors.length === this.productFilters.vendors.length;
        break;
      case 'manufacturer':
        this.allManufacturersSelected =
          this.selectedManufacturers.length ===
          this.productFilters.manufacturers.length;
        break;
      case 'brand':
        this.allBrandsSelected =
          this.selectedBrands.length === this.productFilters.brands.length;
        break;
      case 'category':
        this.allCategoriesSelected =
          this.selectedCategories.length ===
          this.productFilters.categories.length;
        break;
      case 'subCategory':
        this.allSubCategoriesSelected =
          this.selectedSubCategories.length ===
          this.productFilters.subCategories.length;
        break;
    }
  }

  openReportModal(type: ReportType): void {
    this.currentReportType = type;
    this.resetForm();

    if (this.productFilters.vendors.length === 0 && !this.isLoadingFilters) {
      this.loadProductFilters();
    }

    this.showReportModal = true;
  }

  closeReportModal(): void {
    this.showReportModal = false;
    this.isLoading = false;
    this.resetForm();
  }

  // ✅ FIXED: Reset locations state
  resetForm(): void {
    this.fromDate = null;
    this.toDate = null;

    // Reset filter selections
    this.selectedVendors = [];
    this.selectedManufacturers = [];
    this.selectedBrands = [];
    this.selectedCategories = [];
    this.selectedSubCategories = [];
    this.selectedStoreLocations = []; // ✅ ADDED

    // Reset search terms
    this.vendorSearchTerm = '';
    this.manufacturerSearchTerm = '';
    this.brandSearchTerm = '';
    this.categorySearchTerm = '';
    this.subCategorySearchTerm = '';
    this.locationSearchTerm = ''; // ✅ ADDED

    // Reset select all states
    this.allVendorsSelected = false;
    this.allManufacturersSelected = false;
    this.allBrandsSelected = false;
    this.allCategoriesSelected = false;
    this.allSubCategoriesSelected = false;
    this.allLocationsSelected = false; // ✅ ADDED
  }

  get hasValidSelection(): boolean {
    return !!this.fromDate && !!this.toDate;
  }

  // ✅ FIXED: Construct filter payload to include locationId
  private getFilterPayload(): any {
    const filters: any = {};

    // ✅ ADDED: Location filter
    if (this.selectedStoreLocations.length > 0) {
      filters.storeId = this.selectedStoreLocations.filter((id) => id);
    }

    if (this.selectedVendors.length > 0) {
      filters.vendorId = this.selectedVendors.filter((id) => id);
    }
    if (this.selectedManufacturers.length > 0) {
      filters.manufacturerId = this.selectedManufacturers.filter((id) => id);
    }
    if (this.selectedBrands.length > 0) {
      filters.brandId = this.selectedBrands.filter((id) => id);
    }
    if (this.selectedCategories.length > 0) {
      filters.categoryId = this.selectedCategories.filter((id) => id);
    }
    if (this.selectedSubCategories.length > 0) {
      filters.subCategoryId = this.selectedSubCategories.filter((id) => id);
    }
    console.log(`filters: ${JSON.stringify(filters)}`);

    return filters;
  }

  // ✅ FIXED: downloadReport now uses the location filter via filterPayload spread
  downloadReport(): void {
    if (this.isLoading) return;

    if (!this.fromDate || !this.toDate) {
      this.toastr.error('Please select a valid date range.');
      return;
    }

    // // ✅ Ensure at least one location is selected
    // if (this.selectedStoreLocations.length === 0) {
    //   this.toastr.error('Please select at least one store location.');
    //   return;
    // }

    this.isLoading = true;
    let payload: any = {};
    let filename = 'report.xlsx';
    let successMessage = 'Report downloaded successfully.';
    let report$: Observable<Blob>;

    const filterPayload = this.getFilterPayload();
    console.log(`curentReportType: ${this.currentReportType}`);

    // Handle Stock Ledger Report
    if (this.currentReportType === 'stock') {
      payload = {
        reportType: this.selectedStockPreset,
        fromDate: this.fromDate,
        toDate: this.toDate,
        // Store filter — without it the backend merges every store AND every
        // warehouse into one number.
        ...(this.selectedStoreLocations?.length
          ? { storeIds: this.selectedStoreLocations }
          : {}),
      };

      filename = `stock-ledger-${this.fromDate}-to-${this.toDate}.xlsx`;
      successMessage = 'Stock Ledger report downloaded successfully.';
      report$ = this.inventoryService.exportStockLedgerReport(payload);
    }

    // Handle Purchase Tax Report
    else if (this.currentReportType === 'purchase') {
      payload = {
        fromDate: this.fromDate,
        toDate: this.toDate,
      };

      filename = `purchase-tax-report-${this.fromDate}-to-${this.toDate}.xlsx`;
      successMessage = 'Purchase Tax report downloaded successfully.';
      report$ = this.grnService.exportPurchaseTaxReport(payload);
    }

    // Handle all other reports
    else if (this.currentReportType) {
      const reportInfo = this.reportMap[this.currentReportType];

      payload = {
        fromDate: this.fromDate,
        toDate: this.toDate,
        storeId: this.selectedStoreLocations, // ✅ use selected locations
        ...filterPayload,
      };

      const filenameBase = reportInfo.endpoint.replace(/-/g, '_');
      filename = `${filenameBase}-${this.fromDate}_to_${this.toDate}.xlsx`;
      successMessage = `${reportInfo.title} downloaded successfully.`;

      // Progress-aware path. The server builds the whole workbook before it
      // sends anything, so the first phase has no byte counter to show - the
      // UI reports "Preparing..." until the download actually starts.
      report$ = this.reportsService
        .exportReportWithProgress(reportInfo.endpoint, payload)
        .pipe(
          tap((event: HttpEvent<Blob>) => {
            if (event.type === HttpEventType.Sent) {
              this.downloadPhase = 'preparing';
              this.downloadProgress = 0;
              this.downloadedBytes = 0;
            } else if (event.type === HttpEventType.DownloadProgress) {
              this.downloadPhase = 'downloading';
              this.downloadedBytes = event.loaded;
              // Content-Length is absent on chunked responses, so a percentage
              // is not always available - fall back to showing bytes only.
              this.downloadProgress = event.total
                ? Math.round((event.loaded / event.total) * 100)
                : 0;
            }
          }),
          filter(
            (event: HttpEvent<Blob>) => event.type === HttpEventType.Response,
          ),
          map((event: any) => event.body as Blob),
        );
    } else {
      this.isLoading = false;
      return;
    }

    // Download file
    report$.subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);

        this.toastr.success(successMessage);
        this.isLoading = false;
        this.resetDownloadProgress();
        this.closeReportModal();
      },
      error: (err: any) => {
        console.error('Error downloading report:', err);
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorJson = JSON.parse(reader.result as string);
            this.toastr.error(
              errorJson.message || 'Failed to download report.',
            );
          } catch {
            this.toastr.error(
              'Failed to download report due to an unknown error.',
            );
          }
        };
        if (err.error instanceof Blob) reader.readAsText(err.error);
        else
          this.toastr.error(
            err?.error?.message || 'Failed to download report.',
          );

        this.isLoading = false;
        this.resetDownloadProgress();
      },
    });
  }

  // ---------------------------------------------------------------------
  // Stock Take Shrinkage report
  // ---------------------------------------------------------------------

  openStockTakeShrinkageModal(): void {
    this.shrinkStoreId = '';
    this.shrinkStockTakes = [];
    this.shrinkSelected = '';
    this.shrinkPrevSelected = 'auto';
    this.shrinkAuditor = '';
    this.shrinkManager = '';
    if (
      this.productFilters.storeLocations.length === 0 &&
      !this.isLoadingFilters
    ) {
      this.loadProductFilters();
    }
    this.showShrinkageModal = true;
  }

  closeShrinkageModal(): void {
    this.showShrinkageModal = false;
    this.isLoading = false;
    this.resetDownloadProgress();
  }

  onShrinkStoreChange(): void {
    this.shrinkSelected = '';
    this.shrinkPrevSelected = 'auto';
    this.shrinkStockTakes = [];
    if (!this.shrinkStoreId) return;
    this.shrinkLoadingList = true;
    this.reportsService.listShrinkageStockTakes(this.shrinkStoreId).subscribe({
      next: (list) => {
        this.shrinkStockTakes = list || [];
        this.shrinkLoadingList = false;
      },
      error: (err) => {
        console.error('Failed to load stock takes:', err);
        this.toastr.error('Failed to load completed stock takes.');
        this.shrinkLoadingList = false;
      },
    });
  }

  /** `kind:id` composite key so one <select> can list both kinds. */
  shrinkKey(st: StockTakeOption): string {
    return `${st.kind}:${st.id}`;
  }

  shrinkLabel(st: StockTakeOption): string {
    const when = st.completedAt
      ? new Date(st.completedAt).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '';
    const kind = st.kind === 'DRAFT' ? 'Excel upload' : 'Device count';
    return `${st.number} | ${st.mode} | ${kind} | ${when}`;
  }

  /** Previous-stock-take choices: everything completed before the selected one. */
  get shrinkPrevOptions(): StockTakeOption[] {
    const cur = this.shrinkStockTakes.find(
      (s) => this.shrinkKey(s) === this.shrinkSelected,
    );
    if (!cur) return [];
    const curAt = new Date(cur.completedAt).getTime();
    return this.shrinkStockTakes.filter(
      (s) => s !== cur && new Date(s.completedAt).getTime() < curAt,
    );
  }

  onShrinkStockTakeChange(): void {
    this.shrinkPrevSelected = 'auto';
  }

  downloadShrinkageReport(): void {
    if (this.isLoading) return;
    const cur = this.shrinkStockTakes.find(
      (s) => this.shrinkKey(s) === this.shrinkSelected,
    );
    if (!cur) {
      this.toastr.error('Please select a completed stock take.');
      return;
    }

    const payload: any = {
      stockTakeId: cur.id,
      stockTakeKind: cur.kind,
      auditorName: this.shrinkAuditor?.trim() || undefined,
      managerName: this.shrinkManager?.trim() || undefined,
    };
    if (this.shrinkPrevSelected && this.shrinkPrevSelected !== 'auto') {
      const prev = this.shrinkStockTakes.find(
        (s) => this.shrinkKey(s) === this.shrinkPrevSelected,
      );
      if (prev) {
        payload.previousStockTakeId = prev.id;
        payload.previousStockTakeKind = prev.kind;
      }
    }

    this.isLoading = true;
    const filename = `${cur.storeCode || 'stock_take'}_shrinkage_${cur.number}.xlsx`;

    this.reportsService
      .exportReportWithProgress('stock-take-shrinkage', payload)
      .pipe(
        tap((event: HttpEvent<Blob>) => {
          if (event.type === HttpEventType.Sent) {
            this.downloadPhase = 'preparing';
            this.downloadProgress = 0;
            this.downloadedBytes = 0;
          } else if (event.type === HttpEventType.DownloadProgress) {
            this.downloadPhase = 'downloading';
            this.downloadedBytes = event.loaded;
            this.downloadProgress = event.total
              ? Math.round((event.loaded / event.total) * 100)
              : 0;
          }
        }),
        filter(
          (event: HttpEvent<Blob>) => event.type === HttpEventType.Response,
        ),
        map((event: any) => event.body as Blob),
      )
      .subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          window.URL.revokeObjectURL(url);
          this.toastr.success(
            'Stock Take Shrinkage report downloaded successfully.',
          );
          this.isLoading = false;
          this.resetDownloadProgress();
          this.closeShrinkageModal();
        },
        error: (err: any) => {
          console.error('Error downloading shrinkage report:', err);
          const fallback = 'Failed to download Stock Take Shrinkage report.';
          if (err?.error instanceof Blob) {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const json = JSON.parse(reader.result as string);
                this.toastr.error(json.message || fallback);
              } catch {
                this.toastr.error(fallback);
              }
            };
            reader.readAsText(err.error);
          } else {
            this.toastr.error(err?.error?.message || fallback);
          }
          this.isLoading = false;
          this.resetDownloadProgress();
        },
      });
  }

  private resetDownloadProgress(): void {
    this.downloadPhase = 'idle';
    this.downloadProgress = 0;
    this.downloadedBytes = 0;
  }

  /** Human-readable size for the progress label. */
  formatBytes(bytes: number): string {
    if (!bytes) return '0 KB';
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /** Label shown on the Download button while a report is being produced. */
  get downloadLabel(): string {
    if (!this.isLoading) return 'Download Report';
    if (this.downloadPhase === 'downloading') {
      return this.downloadProgress > 0
        ? `Downloading ${this.downloadProgress}%`
        : `Downloading ${this.formatBytes(this.downloadedBytes)}`;
    }
    // Server is still assembling the workbook - no byte counter yet.
    return 'Preparing report...';
  }
}
